import { Fragment, forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { Button, Form } from 'react-bootstrap';

const ListaDicionarioComposta = forwardRef(function ListaDicionarioComposta(
  {
    title,
    items,
    columns,
    editable = false,
    onChangeCode,
    onResolveCode,
    onAddItem,
    onRemoveItem,
    resolvingKeySet,
    onEnterPress,
  },
  ref
) {
  const resolveTimersRef = useRef({});
  // rowRefs.current[index] = HTMLInputElement do código PRINCIPAL (columns[0]) daquela linha
  const rowRefs = useRef([]);
  const pendingFocusIndexRef = useRef(null);

  useEffect(
    () => () => {
      Object.values(resolveTimersRef.current).forEach((timerId) => {
        if (timerId) clearTimeout(timerId);
      });
      resolveTimersRef.current = {};
    },
    []
  );

  useEffect(() => {
    if (pendingFocusIndexRef.current === null) return;
    const targetIndex = pendingFocusIndexRef.current;
    pendingFocusIndexRef.current = null;

    requestAnimationFrame(() => {
      rowRefs.current[targetIndex]?.focus?.();
    });
  }, [items]);

  useImperativeHandle(
    ref,
    () => ({
      focusFirstRow: () => {
        if (!items?.length) {
          pendingFocusIndexRef.current = 0;
          onAddItem?.();
          return;
        }
        requestAnimationFrame(() => {
          rowRefs.current[0]?.focus?.();
        });
      },
    }),
    [items, onAddItem]
  );

  if (!items?.length && !editable) return null;

  const gridTemplateColumns = useMemo(() => {
    const codeColumns = columns.map(() => '76px');
    const textColumns = columns.map((column, index) =>
      index === 0
        ? `minmax(${column.textMinWidth || 220}px, 1.4fr)`
        : `minmax(${column.textMinWidth || 160}px, 1fr)`
    );

    return [...codeColumns, ...textColumns, '48px'].join(' ');
  }, [columns]);

  const primaryCodeKey = columns[0]?.codeKey;

  const handleResolveCode = (index, rawCode, targetCodeKey) => {
    const normalizedCode = String(rawCode || '').trim();
    onResolveCode?.(index, normalizedCode, targetCodeKey);
  };

  const scheduleResolveCode = (index, rawCode, targetCodeKey) => {
    const normalizedCode = String(rawCode || '').trim();
    const timerKey = `${index}:${targetCodeKey}`;

    if (resolveTimersRef.current[timerKey]) {
      clearTimeout(resolveTimersRef.current[timerKey]);
    }

    if (!normalizedCode) {
      onResolveCode?.(index, '', targetCodeKey);
      return;
    }

    resolveTimersRef.current[timerKey] = setTimeout(() => {
      handleResolveCode(index, normalizedCode, targetCodeKey);
      delete resolveTimersRef.current[timerKey];
    }, 450);
  };

  // ENTER (apenas na coluna principal/columns[0]): confirma o código e decide
  // entre remover a linha vazia ou avançar para o próximo campo/lista da tela.
  const handleEnterOnRow = (index, rawCode) => {
    const normalizedCode = String(rawCode || '').trim();
    const timerKey = `${index}:${primaryCodeKey}`;
    if (resolveTimersRef.current[timerKey]) {
      clearTimeout(resolveTimersRef.current[timerKey]);
      delete resolveTimersRef.current[timerKey];
    }
    handleResolveCode(index, normalizedCode, primaryCodeKey);

    if (!normalizedCode) {
      onRemoveItem?.(index);
    }

    onEnterPress?.();
  };

  // Seta para baixo (apenas na coluna principal): cria nova linha e foca nela.
  const handleArrowDownOnRow = (index) => {
    pendingFocusIndexRef.current = index + 1;
    onAddItem?.();
  };

  return (
    <div className="legacy-composite-list mb-3">
      {title ? (
        <div className="legacy-composite-list-header d-flex align-items-center justify-content-between">
          <strong>{title}</strong>
          {editable ? (
            <Button
              size="sm"
              variant="outline-primary"
              className="legacy-list-add"
              onClick={() => onAddItem?.()}
            >
              + Adicionar
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="legacy-composite-scroll">
        <div className="legacy-composite-table" style={{ gridTemplateColumns }} role="table">
          {columns.map((column) => (
            <div
              key={`${column.codeKey}-code-head`}
              className="legacy-composite-head legacy-composite-head-code"
            >
              {column.codeLabel}
            </div>
          ))}
          {columns.map((column) => (
            <div
              key={`${column.codeKey}-text-head`}
              className="legacy-composite-head legacy-composite-head-text"
            >
              {column.textLabel}
            </div>
          ))}
          <div className="legacy-composite-head legacy-composite-head-actions">&nbsp;</div>

          {(items || []).map((item, index) => (
            <Fragment key={`${title || 'composite'}-${index}`}>
              {columns.map((column, columnIndex) => {
                const isPrimaryColumn = columnIndex === 0;

                return (
                  <div
                    key={`${column.codeKey}-code-${index}`}
                    className="legacy-composite-cell legacy-composite-cell-code"
                  >
                    {editable ? (
                      <Form.Control
                        type="text"
                        ref={
                          isPrimaryColumn
                            ? (el) => {
                                rowRefs.current[index] = el;
                              }
                            : undefined
                        }
                        value={item?.[column.codeKey] ?? ''}
                        onChange={(event) => {
                          onChangeCode?.(index, event.target.value, column.codeKey);
                          scheduleResolveCode(index, event.target.value, column.codeKey);
                        }}
                        onBlur={(event) => {
                          const timerKey = `${index}:${column.codeKey}`;
                          if (resolveTimersRef.current[timerKey]) {
                            clearTimeout(resolveTimersRef.current[timerKey]);
                            delete resolveTimersRef.current[timerKey];
                          }
                          handleResolveCode(index, event.target.value, column.codeKey);
                        }}
                        onKeyDown={
                          isPrimaryColumn
                            ? (event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  handleEnterOnRow(index, event.currentTarget.value);
                                } else if (event.key === 'ArrowDown') {
                                  event.preventDefault();
                                  handleArrowDownOnRow(index);
                                }
                              }
                            : undefined
                        }
                        className="legacy-input legacy-composite-code-input"
                        aria-label={`${title || column.textLabel} ${column.codeLabel} ${index + 1}`}
                      />
                    ) : (
                      <div className="legacy-composite-code-value">
                        {item?.[column.codeKey] ?? ''}
                      </div>
                    )}
                  </div>
                );
              })}

              {columns.map((column) => (
                <div
                  key={`${column.codeKey}-text-${index}`}
                  className="legacy-composite-cell legacy-composite-cell-text"
                >
                  <div className="legacy-composite-text-value">
                    {item?.[column.textKey] ?? ''}
                    {column === columns[0] && resolvingKeySet?.has(`${index}`) ? (
                      <span className="legacy-list-resolving"> buscando...</span>
                    ) : null}
                  </div>
                </div>
              ))}

              <div className="legacy-composite-cell legacy-composite-cell-actions">
                {editable ? (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="legacy-list-remove"
                    onClick={() => onRemoveItem?.(index)}
                  >
                    -
                  </Button>
                ) : null}
              </div>
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
});

export default ListaDicionarioComposta;
